import { useEffect, useRef, useState } from "react";

/** Past this the box stops growing and scrolls (about eight lines). */
const MAX_HEIGHT = 220;

/** Grow the box to its content, as the Claude and ChatGPT composers do. */
function fit(el: HTMLTextAreaElement): void {
  el.style.height = "auto";
  const h = Math.min(el.scrollHeight, MAX_HEIGHT);
  el.style.height = `${h}px`;
  el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
}
import { chatApi, type Chip } from "./api";
import { UI, useLang } from "./lang";

export interface Draft {
  text: string;
  chip: string | null;
  attachment: { id: string; name: string } | null;
}

/**
 * Guidance, not a blank canvas (NEW-UX-3): up to four chips above the box,
 * the top chip's words as the prompt. A chip fills, it does not send.
 */
export function Composer({
  workspaceId,
  chips,
  draft,
  setDraft,
  placeholder,
  trust,
  onSend,
  sending,
  onNewTopic,
  onBrowse,
}: {
  workspaceId: string;
  chips: Chip[];
  draft: Draft;
  setDraft: (d: Draft) => void;
  placeholder: string;
  trust: string;
  onSend: () => void;
  sending: boolean;
  /** Start fresh context: a session break in the one thread. */
  onNewTopic?: () => void;
  /** "Browse agents": always the last chip, and it sends at once (agent-library Task 7). */
  onBrowse?: () => void;
}) {
  const { t } = useLang();
  const file = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  // A cleared draft (after Send, or a chip) shrinks the box back to two lines.
  useEffect(() => {
    if (box.current && draft.text === "") {
      box.current.style.height = "";
      box.current.style.overflowY = "";
    }
  }, [draft.text]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const canSend = !sending && (draft.text.trim().length > 0 || draft.attachment !== null);

  return (
    <div className="pwa-compose">
      {chips.length > 0 || onBrowse ? (
        <div className="pwa-chips">
          {chips.map((c) => (
            <button
              key={c.hand_key}
              type="button"
              className={`pwa-chip${draft.chip === c.hand_key ? " on" : ""}`}
              data-testid={`chip-${c.hand_key}`}
              onClick={() => setDraft({ text: t(c), chip: c.hand_key, attachment: null })}
            >
              {t(c)}
            </button>
          ))}
          {onBrowse ? (
            <button type="button" className="pwa-chip" data-testid="chip-browse" disabled={sending} onClick={onBrowse}>
              {t(UI.browseAgents)}
            </button>
          ) : null}
        </div>
      ) : null}
      {draft.attachment ? (
        <div className="pwa-chips">
          <button
            type="button"
            className="pwa-chip on"
            onClick={() => setDraft({ ...draft, attachment: null })}
            aria-label={`Remove ${draft.attachment.name}`}
          >
            {draft.attachment.name} ×
          </button>
        </div>
      ) : null}
      <div className="pwa-box">
        <textarea
          ref={box}
          className="pwa-input"
          data-testid="composer"
          rows={2}
          value={draft.text}
          placeholder={placeholder}
          onChange={(e) => {
            fit(e.target);
            // A chip's agent goes with its words: added to, it stays; replaced,
            // the message is the person's own again and is routed as typed.
            const c = draft.chip ? chips.find((x) => x.hand_key === draft.chip) : undefined;
            const kept = c !== undefined && [c.en, c.ar].some((w) => w !== "" && e.target.value.includes(w));
            setDraft({ ...draft, text: e.target.value, chip: kept ? draft.chip : null });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (canSend) onSend();
            }
          }}
        />
        <div className="pwa-box-bar">
        <button
          type="button"
          className="pwa-icon-btn"
          aria-label={t(UI.attach)}
          title={t(UI.attach)}
          onClick={() => file.current?.click()}
        >
          +
        </button>
        <input
          ref={file}
          type="file"
          accept=".txt,.md,.docx"
          hidden
          data-testid="attach-input"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            setAttachError(null);
            try {
              const up = await chatApi.attach(workspaceId, f);
              setDraft({ text: draft.text, chip: null, attachment: { id: up.attachment_id, name: up.name } });
            } catch (err) {
              setAttachError(err instanceof Error ? err.message : "That file could not be read.");
            }
          }}
        />
        {onNewTopic ? (
          <button type="button" className="pwa-new-topic" data-testid="new-topic" onClick={onNewTopic}>
            {t(UI.newTopic)}
          </button>
        ) : null}
        <span className="pwa-spacer" />
        <button type="button" className="pwa-btn primary" data-testid="send" disabled={!canSend} onClick={onSend}>
          {t(UI.send)}
        </button>
        </div>
      </div>
      {attachError ? (
        <div className="pwa-trust" role="alert" style={{ color: "var(--danger)" }}>
          {attachError}
        </div>
      ) : null}
      <div className="pwa-trust" data-testid="trust">
        {trust}
      </div>
    </div>
  );
}
